import { Injectable, DependencyLifeTime } from "@miracledevs/paradigm-web-di";
import { ContextCollection } from "../../contexts/DependencyContext";
import { CalendarDate } from "./interfaces/calendar-date";
import { capitalize } from "../../shared/helpers";
import { weekdays } from "moment";

@Injectable({ collection: ContextCollection, lifeTime: DependencyLifeTime.Singleton })
export default class DatePickerService
{
    //#region Private properties
    private _locale: string;

    private _today: Date;

    private _localizedMonths: string[];

    private _localizedWeekDays: string[];

    private _currentDate: CalendarDate;
    //#endregion

    //#region Getters
    get localeMonths(): string[] { return this._localizedMonths; }

    get localeWeekDays(): string[] { return this._localizedWeekDays; }

    get currentDate(): CalendarDate { return this._currentDate; }
    //#endregion

    //#region Constructor
    constructor()
    {
        this._locale = "en-us";
        this._today = new Date();
        this._localizedMonths = this.getMonthsNames({ month: "long" });
        this._localizedWeekDays = this.getWeekDaysNames();
        this._currentDate = {
            day: this._today.getDate(),
            month: this._today.getMonth(),
            year: this._today.getFullYear(),
            hours: this._today.getHours(),
            minutes: this._today.getMinutes(),
            seconds: this._today.getSeconds()
        };
    }
    //#endregion

    //#region Public methods
    /**
     * Checks if the current day is today
     * @param date - The date to check
     * @returns A boolean that determines if any given date is today
     */
    checkIfToday(date: CalendarDate): boolean
    {
        return (
            date.day === this._today.getDate() &&
            date.month === this._today.getMonth() &&
            date.year === this._today.getFullYear()
        );
    }

    /**
     * Goes to the previous month
     * @returns The updated date
     */
    goToPreviousMonth(): CalendarDate
    {
        this._currentDate = {
            ...this._currentDate,
            month: this._currentDate.month === 0 ? 11 : this._currentDate.month - 1,
            year: this._currentDate.month === 0 ? this._currentDate.year - 1 : this._currentDate.year
        };

        return this._currentDate;

    }

    /**
     * Goes to the next month
     * @returns The updated date
     */
    goToNextMonth(): CalendarDate
    {
        this._currentDate = {
            ...this._currentDate,
            month: (this._currentDate.month + 1) % 12,
            year: this._currentDate.month === 11 ? this._currentDate.year + 1 : this._currentDate.year
        };

        return this._currentDate;
    }

    /**
     * Jumps to an specific month or/and year
     * @param month - The month
     * @param year - The year
     * @returns The updated date
     */
    goToMonth(month?: number, year?: number): CalendarDate
    {
        this._currentDate = {
            ...this._currentDate,
            month: month ?? this._currentDate.month,
            year: year ?? this._currentDate.year
        };

        return this._currentDate;
    }

    /**
     * Sets the current date values
     * @param date - The updated date values
     * @returns The updated date
     */
    setCurrentDate(date: CalendarDate): CalendarDate
    {
        this._currentDate = date;
        return this._currentDate;
    }

    /**
     * @param month - The month
     * @param year - The year
     * @returns The first day on a month
     */
    getFirstDayOfAMonth(month: number, year: number): number
    {
        return (new Date(year, month)).getDay();
    }

    /**
     * @param month - The month
     * @param year - The year
     * @returns The number of days in a month
     */
    getNumberOfDaysInAMonth(month: number, year: number): number
    {
        /**
            The function new Date(year, month, 32) returns the 32nd day
            after the month started. Subtracting that date from 32,
            gets the final day of that month.
        */
        return 32 - new Date(year, month, 32).getDate();
    }

    /**
     * @param month - The month index
     * @param options - The format options
     * @returns The localized month name
     */
    getLocalizedMonthName(month: number, options?: Intl.DateTimeFormatOptions): string
    {
        let localizedMonths = this._localizedMonths;
        if (options) localizedMonths = this.getMonthsNames(options);
        return localizedMonths[month];
    }

    /**
     * @param day - The day index
     * @param options - The format options
     * @returns The localized day name
     */
    getLocalizedWeekDayName(day: number, options?: Intl.DateTimeFormatOptions): string
    {
        let localizedWeekDays = this.localeWeekDays;
        if (options) localizedWeekDays = this.getWeekDaysNames();
        return localizedWeekDays[day];
    }

    /**
     * @param startDate - The start date
     * @param endDate - The end date
     * @returns An array of dates between two dates
     */
    getDatesInRange(startDate: CalendarDate, endDate: CalendarDate): string[]
    {
        const dates = [];
        const from = [startDate.year, startDate.month + 1, startDate.day].join("-");
        const to = [endDate.year, endDate.month + 1, endDate.day].join("-");
        const dateMove = new Date(from);
        let dateString = from;

        while (dateString < to)
        {
            dateString = dateMove.toLocaleDateString();
            dates.push(dateString);
            dateMove.setDate(dateMove.getDate() + 1);
        };

        return dates;
    }

    /**
     * @param calendarDate - The day, month and year values
     * @param options - The format options
     * @returns A localized date string
     */
    getLocalizedDate(calendarDate?: CalendarDate, options?: Intl.DateTimeFormatOptions): string
    {
        const date = new Date();
        const defaultOptions: Intl.DateTimeFormatOptions = {
            weekday: "short",
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "UTC"
        };

        date.setDate(calendarDate?.day ?? this._today.getDate());
        date.setMonth(calendarDate?.month ?? this._today.getMonth());
        date.setFullYear(calendarDate?.year ?? this._today.getFullYear());
        return date.toLocaleDateString(this._locale, { ...options, ...defaultOptions });
    }
    //#endregion

    //#region Private methods
    /**
     * @param options - The format options
     * @returns An array of localized month names
     */
    private getMonthsNames(options?: Intl.DateTimeFormatOptions): string[]
    {
        const date = new Date(2012, 1, 1, 0, 0, 0, 0);
        const months: string[] = [];

        for (let i = 0; i < 12; i++)
        {
            date.setMonth(i);
            const month = date.toLocaleString(this._locale, options);
            months.push(capitalize(month));
        }
        
        return months;
    }

    /**
     * @param options - The format options
     * @returns An array of localized day names
     */
    private getWeekDaysNames(): string[]
    {
        const days: string[] = [];
        const daysOrder = [1,2,3,4,5,6,7]; // Starts on monday
        
        for (const order of daysOrder)
        {
            days.push(capitalize(weekdays(order)));
        }

        return days;
    }
    //#endregion
}
